import { useUserStore } from '@/stores/user';
import {
  BPLoggeFlowType,
  BPLoggerEventType,
  type AdditionalContext,
  type BusinessProcessLoggerModel,
} from '@onrunning/backstage-models';

import { AppSettings } from '@/constants/app.settings';
import { newRelicAgent } from '@/initializers/new-relic';
import { rollbar } from '@/initializers/rollbar';

export function useBPLogger() {
  const createBusinessProcessBPLog = (
    operationSuccessful: boolean,
    flow: BPLoggeFlowType,
    eventType: BPLoggerEventType,
    additionalContext?: AdditionalContext,
    duration_ms?: number,
  ): BusinessProcessLoggerModel => {
    const { userProfile, selectedAccount, selectedAccountId } = useUserStore();
    const userContext = {
      user_id: userProfile?.profile?.id || '',
      language_iso_code: userProfile?.user?.locale || '',
      selected_account_id: selectedAccountId,
      selected_account_legal_entity: selectedAccount?.legalEntity,
      selected_account_country_id: selectedAccount?.countryId,
      selected_account_billing_country: selectedAccount?.billingCountry,
      selected_account_account_trading_currency: selectedAccount?.accountTradingCurrency,
      selected_account_shipping_location_id: selectedAccount?.shippingLocationId,
      channel_type: 'web',
    };

    const log: BusinessProcessLoggerModel = {
      logger: 'business_process_logger',
      service_version: '1.0.0',
      trace_id: '',
      flow,
      event_type: eventType,
      is_successful: operationSuccessful,
      origin_service: 'backstage-ui',
      environment: AppSettings.env,
      additional_context: { ...userContext, ...additionalContext, duration_ms } as AdditionalContext,
    };

    return log;
  };

  const createBPLogFactory = (flow: BPLoggeFlowType, eventType: BPLoggerEventType) => {
    return (operationSuccessful: boolean, additionalContext?: AdditionalContext, duration_ms?: number) => {
      if (!newRelicAgent) {
        rollbar?.log('New Relic Agent is not available');
        return;
      }
      newRelicAgent.log(
        JSON.stringify(
          createBusinessProcessBPLog(operationSuccessful, flow, eventType, additionalContext, duration_ms),
        ),
      );
    };
  };
  const loginUserBPLog = createBPLogFactory(BPLoggeFlowType.OrderToCash, BPLoggerEventType.Login);
  const logoutUserBPLog = createBPLogFactory(BPLoggeFlowType.OrderToCash, BPLoggerEventType.Logout);
  const submitOrderBPLog = createBPLogFactory(BPLoggeFlowType.OrderToCash, BPLoggerEventType.SubmitOrder);
  const addToCartBPLog = createBPLogFactory(BPLoggeFlowType.OrderToCash, BPLoggerEventType.AddToCart);
  const completePaymentBPLog = createBPLogFactory(BPLoggeFlowType.OrderToCash, BPLoggerEventType.CompletePayment);
  const submitShippingAddressBPLog = createBPLogFactory(
    BPLoggeFlowType.OrderToCash,
    BPLoggerEventType.SubmitShippingAddress,
  );
  const pageViewBPLog = createBPLogFactory(BPLoggeFlowType.OrderToCash, BPLoggerEventType.PageView);
  const createOrderInvoiceBPLog = createBPLogFactory(BPLoggeFlowType.OrderToCash, BPLoggerEventType.CreateOrderInvoice);

  return {
    loginUserBPLog,
    logoutUserBPLog,
    submitOrderBPLog,
    addToCartBPLog,
    completePaymentBPLog,
    submitShippingAddressBPLog,
    pageViewBPLog,
    createOrderInvoiceBPLog,
  };
}
